/*
 *  Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import ApiClient from "@/src/services/apiClient";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromInsurer,
  fromOpposingPartyType,
  fromOrganization,
  getConvertedFormDataBase,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toIban,
  toInsurer,
  toOpposingPartyType,
  toOrganization,
} from "@/src/services/formServiceFunctions";
import { getProductData } from "@/src/services/productService";
import { FormServiceBase, FormServiceSaveProps } from "@/src/types/FormServiceBase";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import _isEmpty from "lodash/isEmpty";
import { Customer } from "@/src/types/Entities/Customer";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_ACCIDENT_BASE]: {
      trafficAccident: [
        "typeOfDamage",
        "clientIsVehicleOwner",
        "typeOfAccident",
        "courseOfEvents",
        "accidentDate",
        "accidentTime",
        "accidentLocalityDescription",
        "policeInvolved",
      ],
      client: ["gender", "givenName", "familyName"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      accidentAddress: ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "client.bankData": ["iban"],
    },
    [applicationStatuses.APPLICATION_STATUS_OPPOSING_PARTY]: {
      trafficAccident: [
        "licensePlateOpposingParty",
        "opposingPartyType",
        "opposingCarLiabilityInsurance",
        "opposingInsurancePolicyNumber",
        "opposingCaseReferenceNumber",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName"],
      "opposingPartyPerson.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      "opposingPartyLegalEntity.organization": ["legalName"],
      "opposingPartyLegalEntity.organization.address": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_DAMAGE]: {
      trafficAccident: [
        "driverOfClientVehicle",
        "licensePlateClientVehicle",
        "vehicleMake",
        "vehicleModel",
        "vehicleId",
        "damageEstimationDocumentType",
        "descriptionInjuries",
        "descriptionDamage",
      ],
      driverPerson: ["gender", "givenName", "familyName"],
      "driverPerson.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      trafficAccident: ["legalExpenseInsurance"],
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      trafficAccident: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "accidentDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "accidentTime",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientIsVehicleOwner",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "policeInvolved",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "caseInsurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
    {
      path: "opposingCarLiabilityInsurance",
      convertFrom: fromOrganization,
      convertTo: toOrganization,
    },
    {
      path: "opposingPartyType",
      convertFrom: fromOpposingPartyType,
      convertTo: toOpposingPartyType,
    },
  ];

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({});

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    refreshCustomer,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};
    const convertedFormData = getConvertedFormDataBase(formData, this.conversionFields);
    let hasCustomerUpdates = false;

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = _pick(convertedFormData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client")) {
      let clientData = _pick(convertedFormData.client, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
      hasCustomerUpdates = true;
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.customer.person.id,
        data: clientData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      let addressData = _pick(convertedFormData.client.residenceAddress, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.customer.person.residenceAddress.id,
        data: addressData,
      });
    }

    if (convertedFormData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let personData = _pick(convertedFormData.opposingPartyPerson, fields.opposingPartyPerson);
        personData = sanitizePersonData(personData);
        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.opposingPartyPerson.id,
          data: personData,
        });
      }

      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = _pick(convertedFormData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyPerson.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (convertedFormData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity.organization")) {
        let opposingPartyLegalEntityData = pickAndConvert(convertedFormData.opposingPartyLegalEntity.organization, fields["opposingPartyLegalEntity.organization"]);
        opposingPartyLegalEntityData = sanitizeOrganizationData(opposingPartyLegalEntityData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.opposingPartyLegalEntity.organization.id,
          data: opposingPartyLegalEntityData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.address")) {
        let addressData = pickAndConvert(
          convertedFormData.opposingPartyLegalEntity.organization.address,
          fields["opposingPartyLegalEntity.organization.address"]
        );
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyLegalEntity.organization.address.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "accidentAddress")) {
      let addressData = _pick(convertedFormData.accidentAddress, fields.accidentAddress);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.accidentAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "driverPerson")) {
      let personData = _pick(convertedFormData.driverPerson, fields.driverPerson);
      personData = sanitizePersonData(personData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.driverPerson.id,
        data: personData,
      });
    }

    if (_has(fields, "driverPerson.residenceAddress")) {
      let addressData = _pick(convertedFormData.driverPerson.residenceAddress, fields["driverPerson.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.driverPerson.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "client.bankData")) {
      const bankData = pickAndConvert(convertedFormData.client.bankData, fields["client.bankData"]);
      if (bankData.iban) {
        bankData.iban = toIban(bankData.iban);
      } else {
        delete bankData.iban;
      }
      if (!_isEmpty(bankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: product.client.bankData.id,
          data: bankData,
        });
      }
    }

    if (_has(fields, "caseInsurance")) {
      let caseInsuranceData = _pick(convertedFormData.caseInsurance, fields["caseInsurance"]);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    let trafficAccidentData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      trafficAccidentData.applicationStatus = nextApplicationStatus;
    }

    if (hasCustomerUpdates) {
      await refreshCustomer();
    }

    if (_has(fields, "trafficAccident")) {
      trafficAccidentData = _merge(trafficAccidentData, _pick(convertedFormData, fields.trafficAccident));
    }
    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: trafficAccidentData,
    });
  };

  getAvailableSteps = () => {
    let availableSteps = [];
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_ACCIDENT_BASE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_OPPOSING_PARTY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DAMAGE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    return availableSteps;
  };
}

export default new FormService();
